import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function Login() {
  const { loginWithSSO } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  async function handleMicrosoftLogin() {
    setIsLoading(true);
    try {
      await loginWithSSO();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "SSO login failed");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="bg-gradient-to-br from-[#144677] via-[#1a5691] to-[#0d3557] min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Logo section with enhanced styling */}
        <div className="mb-8 text-center animate-fade-in">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 inline-block mb-4">
            <img
              src="/ATHENA_ICON_CORAL.png"
              alt="Athena logo"
              className="mx-auto w-60 drop-shadow-lg"
              loading="lazy"
              width={240}
              height={72}
            />
          </div>
        </div>

        {/* Login card with glassmorphism effect */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 w-full border border-white/20 transform transition-all duration-300 hover:shadow-3xl">
          <div className="mb-8">
            <h1 className="font-bold text-center text-3xl mb-3 text-gray-800 tracking-tight">
              Welcome back!
            </h1>
            <p className="text-center text-gray-600 text-base">
              We missed you! Please sign in to continue.
            </p>
          </div>

          {/* Enhanced Microsoft login button */}
          <button
            onClick={handleMicrosoftLogin}
            disabled={isLoading}
            className="group w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3 relative overflow-hidden"
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

            {/* Microsoft icon */}
            <svg
              className={`w-6 h-6 transition-transform duration-200 ${
                isLoading ? "animate-spin" : "group-hover:rotate-12"
              }`}
              viewBox="0 0 23 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="11" height="11" fill="white" />
              <rect x="12" width="11" height="11" fill="white" />
              <rect y="12" width="11" height="11" fill="white" />
              <rect x="12" y="12" width="11" height="11" fill="white" />
            </svg>

            <span className="relative z-10">
              {isLoading ? "Signing in..." : "Sign in with Microsoft"}
            </span>
          </button>

          {/* Additional info */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Secure authentication powered by Microsoft
            </p>
          </div>
        </div>

        {/* Footer text */}
        {/* <div className="mt-6 text-center">
          <p className="text-white/80 text-sm">
            By signing in, you agree to our Terms of Service
          </p>
        </div> */}
      </div>
    </div>
  );
}
